import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { DATE } from "../../services/moksha-reservations/format";
import { addBlockedReason } from "./reservationDraft";
import { capacityLabel, focusRing } from "./presentation";
import { SectionTitle, STALE_OPACITY, useColors } from "./ui";

const COLUMNS = "2fr 1fr 2fr 1.4fr";

function AvailabilityTable({ availability, checkedDates, lines, stale, onAdd }) {
    const colors = useColors();
    return (
        <Box sx={{ opacity: stale ? STALE_OPACITY : 1 }} aria-disabled={stale}>
            <SectionTitle
                extra={
                    <Typography variant="body2" color={colors.grey[300]}>
                        {moment(checkedDates.checkin, DATE).format("DD MMM")} –{" "}
                        {moment(checkedDates.checkout, DATE).format("DD MMM YYYY")}
                    </Typography>
                }
            >
                Availability
            </SectionTitle>
            {!availability.roomtypes?.length ? (
                <Typography color={colors.grey[300]}>No room types on this property are mapped to its PMS yet.</Typography>
            ) : (
                <Box role="table" aria-label="Room availability">
                    <Box
                        role="row"
                        sx={{ display: { xs: "none", sm: "grid" } }}
                        gridTemplateColumns={COLUMNS}
                        gap={2}
                        px={1.5}
                        pb={1}
                        color={colors.grey[300]}
                    >
                        <Typography role="columnheader" variant="body2">Roomtype</Typography>
                        <Typography role="columnheader" variant="body2">Free for whole stay</Typography>
                        <Typography role="columnheader" variant="body2">Rate plans</Typography>
                        <Typography role="columnheader" variant="body2" textAlign="right">
                            Action
                        </Typography>
                    </Box>
                    <Stack spacing={1}>
                        {availability.roomtypes.map((rt) => {
                            const soldOut = !(rt.available_rooms > 0);
                            const reason = addBlockedReason(rt, lines);
                            return (
                                <Box
                                    role="row"
                                    key={rt.roomtype_id}
                                    display="grid"
                                    gridTemplateColumns={{ xs: "1fr auto", sm: COLUMNS }}
                                    gap={{ xs: 1, sm: 2 }}
                                    alignItems="center"
                                    p={1.5}
                                    borderRadius={2}
                                    backgroundColor={colors.primary[500]}
                                >
                                    <Box role="cell">
                                        <Typography fontWeight={600}>{rt.name}</Typography>
                                        {capacityLabel(rt) && (
                                            <Typography variant="body2" color={colors.grey[300]}>
                                                {capacityLabel(rt)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box role="cell" textAlign={{ xs: "right", sm: "left" }}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={soldOut ? "error" : "success"}
                                            label={soldOut ? "0 free" : `${rt.available_rooms} free`}
                                        />
                                    </Box>
                                    <Box role="cell" display="flex" flexWrap="wrap" gap={0.5}>
                                        {rt.meal_plans?.length ? (
                                            rt.meal_plans.map((mp) => (
                                                <Chip key={mp.meal_plan_id} size="small" label={mp.name?.toUpperCase()} />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color={colors.grey[300]}>
                                                No meal plans
                                            </Typography>
                                        )}
                                    </Box>
                                    <Stack role="cell" direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                        {reason && (
                                            <Typography variant="body2" color={colors.grey[300]}>
                                                {reason}
                                            </Typography>
                                        )}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Add />}
                                            disabled={!!reason || stale}
                                            onClick={() => onAdd(rt)}
                                            aria-label={`Add ${rt.name}`}
                                            sx={focusRing(colors)}
                                        >
                                            Add
                                        </Button>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

export default AvailabilityTable;
